/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, computed, runInAction, observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Icon, Popup } from 'semantic-ui-react';
import { displayError } from '@amzn/base-ui/dist/helpers/notification';
import Dropdown from '@amzn/base-ui/dist/parts/helpers/fields/DropDown';
import Form from '@amzn/base-ui/dist/parts/helpers/fields/Form';
import Input from '@amzn/base-ui/dist/parts/helpers/fields/Input';
import SelectionButtons from '@amzn/base-ui/dist/parts/helpers/fields/SelectionButtons';
import TextArea from '@amzn/base-ui/dist/parts/helpers/fields/TextArea';
import Range from '@amzn/base-ui/dist/parts/helpers/fields/Range';

import { getCreateInternalEnvForm } from '../../../models/forms/CreateInternalEnvForm';
import { getAvailableRegions } from '../../../helpers/api';
import { isHyperthreadingLockedInstanceType } from '../../../helpers/instance-config';
import { getRegionDisplayText } from '../../../models/constants/aws-regions';
import { categories } from '../../../models/studies/categories';
import { isStoreReady } from '@amzn/base-ui/dist/models/BaseStore';
import SelectConfigurationTabs from './SelectConfigurationTabs';
import { getRootVolumeSizeRange } from './EnvTypeAdditionalInfo';
import FloatingNavBar from './FloatingNavBar';
import Logos from './Logos';
import StepGuideCard from '../../helpers/StepGuideCard';

// expected props
// - onPrevious (via props)
// - onNext (via props) a function is called with the form data
// - envTypeId (via props)
// - configurations (via props)
// - title (via props)
// - defaultCidr (via props)
// - clientInformationStore (via injection)
// - userStore (via injection)
// - projectsStore (via injection)
class CreateInternalEnvForm extends React.Component {
  constructor(props) {
    super(props);
    this.topBarRef = React.createRef();
    const primaryRegion = process.env.REACT_APP_AWS_REGION;
    runInAction(() => {
      this.primaryRegion = primaryRegion;
      this.availableRegions = [primaryRegion];
      this.loadingRegions = false;
      const regionOptions = [{ key: primaryRegion, value: primaryRegion, text: getRegionDisplayText(primaryRegion) }];
      const rootVolumeSizeRange = getRootVolumeSizeRange(this.props.title);
      this.projects = this.getProjects();
      const projectIdOptions = this.getProjectIdOptions();
      const userName = this.userStore?.user?.displayName || 'User';
      this.form = getCreateInternalEnvForm({
        projectIdOptions,
        cidr: this.isAppStreamEnabled ? undefined : this.props.defaultCidr,
        regionOptions,
        rootVolumeSizeRange,
        userName,
      });

      this.forceLockHyperthreading = false;
      this.forceLockCrossCommunication = false;
      const title = (this.props.title || '').toLowerCase();
      const osKeywords = ['windows', 'ubuntu', 'linux', 'redhat', 'rhel'];
      const isSageMaker = title.includes('jupyterlab') && !osKeywords.some(keyword => title.includes(keyword));
      if (isSageMaker) {
        this.forceLockHyperthreading = true;
        this.form.$('enableHyperthreading').set(false);
        this.forceLockCrossCommunication = true;
        this.form.$('crossCommunication').set(false);
      }
      this.hyperthreadingLocked = this.forceLockHyperthreading;
      this.rootVolumeSizeRange = rootVolumeSizeRange;
      this.advancedExpanded = false;
      this.regionFading = false;
      this.checkCrossCommAvailability(projectIdOptions[0].value);

      if (projectIdOptions.length > 0) {
        this.handleProjectChange(null, { value: projectIdOptions[0].value });
      }
    });
  }

  componentDidUpdate(prevProps) {
    // If the CIDR field exists but is empty, and the defaultCidr prop just became available
    // (IP address loaded asynchronously), fill it in.
    if (
      this.props.defaultCidr &&
      prevProps.defaultCidr !== this.props.defaultCidr &&
      this.form.has('cidr') &&
      !this.form.$('cidr').value
    ) {
      this.form.$('cidr').set(this.props.defaultCidr);
    }
  }

  getProjectIdOptions() {
    const store = this.userStore;
    if (!this.isAppStreamEnabled) return store.projectIdDropdown;

    const projects = this.projects;
    const filteredProjects = _.filter(projects, proj => proj.isAppStreamConfigured);
    if (_.isEmpty(filteredProjects)) return [];

    const filteredProjectIds = _.map(filteredProjects, proj => proj.id);
    const retVal = _.filter(store.projectIdDropdown, proj => _.includes(filteredProjectIds, proj.key));

    return retVal;
  }

  get isAppStreamEnabled() {
    return process.env.REACT_APP_IS_APP_STREAM_ENABLED === 'true';
  }

  get envTypeId() {
    return this.props.envTypeId;
  }

  get configurations() {
    return this.props.configurations;
  }

  get userStore() {
    return this.props.userStore;
  }

  get selectedStudyCounts() {
    const selection = this.props.filesSelection;
    const storesMap = this.props.studiesStoresMap;
    const result = { studies: 0, licenses: 0, openData: 0, studyNames: [], licenseNames: [], openDataNames: [] };

    if (!selection || selection.empty) return result;

    // Build a lookup of study id -> study object from all stores
    const studyLookup = {};
    _.forEach(categories, category => {
      const store = storesMap[category.id];
      if (store && isStoreReady(store)) {
        store.list.forEach(s => { studyLookup[s.id] = s; });
      }
    });

    selection.files.forEach(file => {
      const study = studyLookup[file.id];
      const name = (study && study.name) || file.name || file.id;
      if (!study) {
        result.studies += 1;
        result.studyNames.push(name);
      } else if (study.isLicenseStudy) {
        result.licenses += 1;
        result.licenseNames.push(name);
      } else if (study.isOpenDataStudy) {
        result.openData += 1;
        result.openDataNames.push(name);
      } else {
        result.studies += 1;
        result.studyNames.push(name);
      }
    });

    return result;
  }

  getProjects() {
    const store = this.getProjectsStore();
    return store.list;
  }

  getProjectsStore() {
    const store = this.props.projectsStore;
    store.load();
    return store;
  }

  handleProjectChange = async (event, data) => {
    const { value: projectId } = data;
    this.checkCrossCommAvailability(projectId);

    if (!projectId) {
      runInAction(() => {
        this.availableRegions = [this.primaryRegion];
        this.rebuildForm();
        this.autoSelectFirstConfig();
      });
      return;
    }

    runInAction(() => {
      this.loadingRegions = true;
    });

    try {
      const { regions } = await getAvailableRegions(projectId);
      runInAction(() => {
        this.availableRegions = regions || [];
        this.rebuildForm();
        this.autoSelectFirstConfig();
      });
    } catch (error) {
      runInAction(() => {
        this.availableRegions = [this.primaryRegion];
        this.rebuildForm();
        this.autoSelectFirstConfig();
      });
    } finally {
      runInAction(() => {
        this.loadingRegions = false;
      });
    }
  };

  // eslint-disable-next-line consistent-return
  handlePrevious = () => {
    if (_.isFunction(this.props.onPrevious)) return this.props.onPrevious();
  };

  handleRegionChange = (event, data) => {
    const { value: region } = data;
    this.form.$('targetRegion').set(region);
    // Auto-select first config for the new region
    this.autoSelectFirstConfig();

    // Brief fade to signal the table content has changed
    this.regionFading = true;
    clearTimeout(this._regionFadeTimer);
    this._regionFadeTimer = setTimeout(() => {
      runInAction(() => { this.regionFading = false; });
    }, 750);
  };

  handleConfigChange = () => {
    // No region recalculation needed — configs are already filtered by region
  };

  autoSelectFirstConfig() {
    const filtered = this.filteredConfigurations;
    const field = this.form.$('envTypeConfigId');
    if (filtered.length > 0) {
      field.set(filtered[0].id);
      field.resetValidation();
      // The lock has to follow the auto-selected config as well as a clicked one, or a GPU config
      // that only runs one thread per core is preselected with the hyperthreading choice left open.
      this.handleHyperthreadingLock(isHyperthreadingLockedInstanceType(filtered[0].instanceType));
    } else {
      field.clear();
      field.resetValidation();
      this.handleHyperthreadingLock(false);
    }
  }

  get filteredConfigurations() {
    const selectedRegion = this.form && this.form.has('targetRegion') ? this.form.$('targetRegion').value : this.primaryRegion;
    if (!selectedRegion) return this.configurations;

    return _.filter(this.configurations, config => {
      const allowedRegions = _.get(config, 'allowedRegions', []);
      if (_.isEmpty(allowedRegions)) {
        // No allowedRegions means primary region only
        return selectedRegion === this.primaryRegion;
      }
      return allowedRegions.includes(selectedRegion);
    });
  }

  rebuildForm() {
    const regionOptions = this.availableRegions.map((r) => ({ key: r, value: r, text: getRegionDisplayText(r) }));
    const oldValues = this.form.values();
    this.form = getCreateInternalEnvForm({
      projectIdOptions: this.getProjectIdOptions(),
      cidr: this.isAppStreamEnabled ? undefined : this.props.defaultCidr,
      regionOptions,
      rootVolumeSizeRange: this.rootVolumeSizeRange,
      userName: this.userStore?.user?.displayName || 'User',
    });
    Object.keys(oldValues).forEach((key) => {
      if (this.form.has(key) && oldValues[key]) {
        if (key === 'targetRegion' && !this.availableRegions.includes(oldValues[key])) return;
        this.form.$(key).set(oldValues[key]);
      }
    });

    if (this.forceLockHyperthreading) {
      this.form.$('enableHyperthreading').set(false);
    }
    if (this.forceLockCrossCommunication || this.lockCrossCommunication) {
      this.form.$('crossCommunication').set(false);
    }
  }

  // eslint-disable-next-line consistent-return
  handleNext = async (form) => {
    const data = { ...form.values(), envTypeId: this.envTypeId };

    try {
      await this.props.onNext(data);
    } catch (error) {
      displayError(error);
    }
  };

  handleVolumeChange = (event, { value }) => {
    const volume = Number(value);
    this.form.$('rootVolume').set(volume);
  };

  toggleAdvancedOptions = () => {
    this.advancedExpanded = !this.advancedExpanded;
  };

  handleHyperthreadingChange = (event, data) => {
    const { value } = data;
    this.form.$('enableHyperthreading').set(value);
  };

  handleHyperthreadingLock = (lock, available = true) => {
    if (this.forceLockHyperthreading) return;
    if (lock) {
      this.handleHyperthreadingChange(null, { value: available });
      this.hyperthreadingLocked = true;
    } else {
      this.hyperthreadingLocked = false;
    }
  };

  handleExposeOptionOnChange = (__, { value }) => {
    this.form.$('crossCommunication').set(value);
  };

  checkCrossCommAvailability = projectId => {
    if (this.forceLockCrossCommunication) {
      runInAction(() => {
        this.lockCrossCommunication = true;
        this.form.$('crossCommunication').set(false);
      });
      return;
    }
    const project = _.find(this.projects, { id: projectId });
    const allowCrossCommunication = !!project?.projectCrossCommunication;
    runInAction(() => {
      this.lockCrossCommunication = !allowCrossCommunication;
      this.form.$('crossCommunication').set(allowCrossCommunication ? true : false);
    });
  };

  render() {
    return <div>{this.renderForm()}</div>;
  }

  renderTopNav(processing, hasErrors) {
    return (
      <div className="setup-top-bar" ref={this.topBarRef}>
        <div className="setup-top-bar-left">
          <span className="setup-top-bar-title">
            <Icon name="hdd outline" /> Configure Workspace
          </span>
        </div>
        <div className="setup-top-bar-nav">
          <button
            type="button"
            className="workspace-action-btn workspace-action-btn-secondary"
            disabled={processing}
            onClick={this.props.onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="workspace-action-btn workspace-action-btn-secondary"
            disabled={processing}
            onClick={this.handlePrevious}
          >
            <Icon name="arrow left" /> Back
          </button>
          <button
            type="submit"
            className="workspace-action-btn workspace-action-btn-primary"
            disabled={processing || hasErrors}
          >
            Create Workspace <Icon name="check" />
          </button>
        </div>
        <div className="setup-top-bar-description">
          Name your workspace, choose your hardware configuration and create it.
        </div>
      </div>
    );
  }

  renderGuideCard() {
    return (
      <StepGuideCard guideId="step-configure-workspace" title="Configuring your workspace">
        On this page, you will give your workspace a name and description, then choose a
        hardware configuration. The configuration determines how powerful your machine will
        be. Smaller sizes work well for everyday tasks, while larger sizes are better suited
        for heavy computation or GPU workloads. You can also adjust the storage volume size
        if you need more disk space. Once you are happy with your selections,
        click <strong>Create Workspace</strong> to launch it.
      </StepGuideCard>
    );
  }

  renderFloatingNav(processing, hasErrors) {
    return (
      <FloatingNavBar sentinelRef={this.topBarRef}>
        <button
          type="button"
          className="workspace-action-btn workspace-action-btn-secondary"
          disabled={processing}
          onClick={this.props.onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="workspace-action-btn workspace-action-btn-secondary"
          disabled={processing}
          onClick={this.handlePrevious}
        >
          <Icon name="arrow left" /> Back
        </button>
        <button
          type="submit"
          className="workspace-action-btn workspace-action-btn-primary"
          disabled={processing || hasErrors}
        >
          Create Workspace <Icon name="check" />
        </button>
      </FloatingNavBar>
    );
  }

  renderButtons() {
    return (
      <div className="setup-top-bar">
        <div className="setup-top-bar-left">
          <span className="setup-top-bar-title">
            <Icon name="hdd outline" /> Configure Workspace
          </span>
        </div>
        <div className="setup-top-bar-nav">
          <button
            type="button"
            className="workspace-action-btn workspace-action-btn-secondary"
            onClick={this.handlePrevious}
          >
            <Icon name="arrow left" /> Back
          </button>
          <button
            type="button"
            className="workspace-action-btn workspace-action-btn-primary"
            disabled
          >
            Create Workspace <Icon name="check" />
          </button>
        </div>
        <div className="setup-top-bar-description">
          Name your workspace, choose your hardware configuration and create it.
        </div>
      </div>
    );
  }

  renderMissingAppStreamConfig() {
    return (
      <>
        {this.renderButtons()}
        <div className="workspace-empty-state mt2">
          <div className="workspace-empty-state-icon workspace-empty-state-icon-warning">
            <Icon name="lock" />
          </div>
          <div className="workspace-empty-state-title">Missing AppStream Configuration</div>
          <div className="workspace-empty-state-subtitle">
            Your projects are not associated with an AppStream-configured account.
          </div>
          <div className="workspace-empty-state-hint">
            Please contact your administrator to resolve this issue.
          </div>
        </div>
      </>
    );
  }

  renderForm() {
    const form = this.form;
    const shouldAskforCidr = !_.isUndefined(this.props.defaultCidr) && !this.isAppStreamEnabled;
    const configurations = this.configurations;
    const cidrField = shouldAskforCidr ? form.$('cidr') : undefined;
    const hasRegionField = form.has('targetRegion');
    const noRegionsAvailable = hasRegionField && this.filteredConfigurations.length === 0 && this.form.$('targetRegion').value;
    const volumeField = form.$('rootVolume');

    const projects = this.getProjectIdOptions();
    if (this.isAppStreamEnabled && _.isEmpty(projects)) {
      return this.renderMissingAppStreamConfig();
    }

    const hyperthreadingOptions = [
      { text: 'Enabled', value: true },
      { text: 'Disabled', value: false },
    ];

    const exposeOptions = [
      { text: 'Enabled', value: true },
      { text: 'Disabled', value: false },
    ];

    return (
      <Form form={form} onCancel={this.handlePrevious} onSuccess={this.handleNext}>
        {({ processing, errors, onCancel }) => {
          const hasErrors = errors && errors.length > 0;
          return (
          <>
            {this.renderTopNav(processing, hasErrors)}
            {this.renderGuideCard()}
            {this.renderFloatingNav(processing, hasErrors)}

            {/* Selected workspace type and mounted data summary */}
            <div className="form-section-selected-type">
              <span className="form-section-selected-type-label">Selected Image:</span>
              <Logos.Dynamic name={this.props.title} size="large" color="var(--rmit-blue)" />
              <span className="form-section-selected-type-name">{this.props.title}</span>
              <div className="form-section-selected-type-sep" />
              <span className="form-section-selected-type-label">Mounted Data:</span>
              <div className="form-section-selected-data-items">
                <Popup
                  trigger={
                    <div className="form-section-data-chip">
                      <Icon name="database" style={{ margin: 0, lineHeight: 1, height: '1em' }} />
                      <span>{this.selectedStudyCounts.studies} {this.selectedStudyCounts.studies === 1 ? 'Study' : 'Studies'}</span>
                    </div>
                  }
                  content={this.selectedStudyCounts.studyNames.length > 0 ? this.selectedStudyCounts.studyNames.join(', ') : 'No studies selected'}
                  position="bottom center"
                  inverted
                  size="small"
                />
                <Popup
                  trigger={
                    <div className="form-section-data-chip">
                      <Icon name="key" style={{ margin: 0, lineHeight: 1, height: '1em' }} />
                      <span>{this.selectedStudyCounts.licenses} {this.selectedStudyCounts.licenses === 1 ? 'License' : 'Licenses'}</span>
                    </div>
                  }
                  content={this.selectedStudyCounts.licenseNames.length > 0 ? this.selectedStudyCounts.licenseNames.join(', ') : 'No licenses selected'}
                  position="bottom center"
                  inverted
                  size="small"
                />
                <Popup
                  trigger={
                    <div className="form-section-data-chip">
                      <Icon name="globe" style={{ margin: 0, lineHeight: 1, height: '1em' }} />
                      <span>{this.selectedStudyCounts.openData} Open Data</span>
                    </div>
                  }
                  content={this.selectedStudyCounts.openDataNames.length > 0 ? this.selectedStudyCounts.openDataNames.join(', ') : 'No open datasets selected'}
                  position="bottom center"
                  inverted
                  size="small"
                />
              </div>
            </div>

            {/* Section 1: General */}
            <div className="form-section">
              <div className="form-section-header">
                <Icon name="info circle" />
                <h4 className="form-section-title">General</h4>
              </div>
              <div className="form-section-body">
                <div className="form-section-grid">
                  <Input dataTestId="workspace-name" field={form.$('name')} name="Workspace Name (Required)" className="form-field" showErrorLabel={false} />
                  <Dropdown dataTestId="project-id" field={form.$('projectId')} fluid selection className="form-field" showErrorLabel={false} onChange={this.handleProjectChange} />
                  <TextArea dataTestId="description-text-area" field={form.$('description')} name="Description (Required)" className="form-field full-width" showErrorLabel={false} />
                </div>
              </div>
            </div>

            {/* Section 2: Instance Configuration */}
            <div className="form-section">
              <div className="form-section-header">
                <Icon name="microchip" />
                <h4 className="form-section-title">Instance Configuration</h4>
              </div>
              <div className="form-section-body">
                <div className="form-section-grid">
                  {hasRegionField && (
                    <Dropdown
                      dataTestId="target-region"
                      field={form.$('targetRegion')}
                      fluid
                      selection
                      className="form-field"
                      onChange={this.handleRegionChange}
                    />
                  )}
                  <Range {...this.rootVolumeSizeRange} dataTestId="root-volume" field={form.$('rootVolume')} onChange={this.handleVolumeChange} className="form-field" showErrorLabel={false} />
                </div>

                {hasRegionField && form.$('targetRegion').value && form.$('targetRegion').value !== this.primaryRegion && (
                  <div className="workspace-alert workspace-alert-warning mt2">
                    <Icon name="warning sign" />
                    <span>
                      <strong>Non-primary region selected.</strong> Data processed in this workspace will be stored
                      in <strong>{getRegionDisplayText(form.$('targetRegion').value)}</strong>, outside Australia. Please ensure your
                      research data governance requirements permit processing data in this region before proceeding.
                    </span>
                  </div>
                )}

                {volumeField.value >= this.rootVolumeSizeRange.max * 0.75 && (
                  <div className="workspace-alert workspace-alert-warning mt2">
                    <Icon name="warning sign" />
                    <span><strong>Do you require more storage space?</strong> If you require a larger volume size, please contact race@rmit.edu.au!</span>
                  </div>
                )}

                {noRegionsAvailable && (
                  <div className="workspace-alert workspace-alert-warning mt2">
                    <Icon name="warning sign" />
                    <span><strong>No configurations available.</strong> There are no hardware configurations available in the selected region. Please try a different region or contact your administrator.</span>
                  </div>
                )}

                <SelectConfigurationTabs
                  configurations={this.filteredConfigurations}
                  formField={form.$('envTypeConfigId')}
                  handleHyperthreadingLock={this.handleHyperthreadingLock}
                  onChange={this.handleConfigChange}
                  fading={this.regionFading}
                  region={form.has('targetRegion') ? form.$('targetRegion').value : this.primaryRegion}
                />

                {(() => {
                  const selectedConfigId = form.$('envTypeConfigId').value;
                  const selectedConfig = this.filteredConfigurations.find(c => c.id === selectedConfigId);
                  const desc = selectedConfig ? (selectedConfig.description || selectedConfig.descHtml || '') : '';
                  if (desc.toLowerCase().includes('dcv not supported')) {
                    return (
                      <div className="workspace-alert workspace-alert-warning mt2">
                        <Icon name="warning sign" />
                        <span><strong>DCV not supported.</strong> The selected instance type does not support DCV desktop streaming connections. You will need to connect using RDP or SSH instead.</span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            {/* Section 3: Advanced Options (collapsed by default) */}
            <div className="form-section">
              <button
                type="button"
                className="form-section-header"
                onClick={this.toggleAdvancedOptions}
                style={{ cursor: 'pointer', width: '100%', background: 'none', border: 'none', fontFamily: 'inherit', padding: '16px 20px' }}
                aria-expanded={this.advancedExpanded}
              >
                <Icon name={this.advancedExpanded ? 'chevron down' : 'chevron right'} style={{ margin: 0, lineHeight: 1, height: '1em', marginRight: '8px' }} />
                <h4 className="form-section-title" style={{ margin: 0 }}>Advanced Options</h4>
              </button>
              {this.advancedExpanded && (
              <div className="form-section-body">
                <div className="form-section-grid">
                  <SelectionButtons
                    field={form.$('enableHyperthreading')}
                    options={hyperthreadingOptions}
                    alt="true"
                    className="form-field"
                    disabled={this.hyperthreadingLocked}
                    onChange={this.handleHyperthreadingChange}
                  />
                  <SelectionButtons
                    field={form.$('crossCommunication')}
                    options={exposeOptions}
                    alt="true"
                    className="form-field"
                    onChange={this.handleExposeOptionOnChange}
                    disabled={this.lockCrossCommunication}
                  />
                </div>

                {shouldAskforCidr && (
                  <div style={{ marginTop: '20px' }}>
                    <div className="form-section-grid">
                      <Input field={cidrField} dataTestId="cidr" className="form-field" showErrorLabel={false} />
                    </div>
                    {!_.isEmpty(cidrField.value) && cidrField.value.split('/')[1] < 24 && (
                      <div className="workspace-alert workspace-alert-warning mt2">
                        <Icon name="warning sign" />
                        <span><strong>Wide CIDR block detected.</strong> One or more CIDR blocks entered might be too wide and could allow workspace access to a vast number of IP ranges. Please proceed only after you ensure this is intended.</span>
                      </div>
                    )}
                    {!_.isEmpty(cidrField.value) &&
                      (cidrField.value.split('/')[1] < 16 || cidrField.value.toString().includes('0.0.0.0')) && (
                        <div className="workspace-alert workspace-alert-error mt2">
                          <Icon name="exclamation circle" />
                          <span><strong>Open CIDR block detected.</strong> One of the CIDR blocks entered is vulnerable as it makes the workspace accessible to anyone.</span>
                        </div>
                      )}
                  </div>
                )}
              </div>
              )}
            </div>
          </>
          );
        }}
      </Form>
    );
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(CreateInternalEnvForm, {
  form: observable,
  availableRegions: observable,
  loadingRegions: observable,
  envTypeId: computed,
  configurations: computed,
  filteredConfigurations: computed,
  userStore: computed,
  isAppStreamEnabled: computed,
  selectedStudyCounts: computed,
  handlePrevious: action,
  handleProjectChange: action,
  handleRegionChange: action,
  handleConfigChange: action,
  rebuildForm: action,
  handleHyperthreadingLock: action,
  projects: observable,
  hyperthreadingLocked: observable,
  forceLockHyperthreading: observable,
  forceLockCrossCommunication: observable,
  rootVolumeSizeRange: observable,
  lockCrossCommunication: observable,
  advancedExpanded: observable,
  regionFading: observable,
  toggleAdvancedOptions: action,
});

export default inject('userStore', 'projectsStore', 'clientInformationStore', 'filesSelection', 'studiesStoresMap')(observer(CreateInternalEnvForm));
